import fs from 'node:fs';
import path from 'node:path';
import { logger } from '../utils/logger.js';

interface UnpackOptions {
  noSignatures?: boolean;
}

/**
 * Check if this is a valid Standard Agents project.
 */
function isStandardAgentsProject(): boolean {
  // Check for agents directory
  if (!fs.existsSync('agents')) {
    return false;
  }

  // Check for @standardagents/builder dependency
  try {
    const pkgJson = JSON.parse(fs.readFileSync('package.json', 'utf-8'));
    const deps = { ...pkgJson.dependencies, ...pkgJson.devDependencies };
    return '@standardagents/builder' in deps;
  } catch {
    return false;
  }
}

/**
 * Unpack a packed agent to the local agents directory.
 */
export async function unpack(pkgIdentifier: string | undefined, options: UnpackOptions) {
  logger.info('Unpacking agent...\n');

  // Verify this is a Standard Agents project
  if (!isStandardAgentsProject()) {
    logger.error('This does not appear to be a Standard Agents project.');
    logger.info('Make sure you have @standardagents/builder installed and an agents/ directory.');
    process.exit(1);
  }

  const rootDir = process.cwd();

  // Dynamic import of unpacking service
  let UnpackingService: any;
  try {
    const builder = await import('@standardagents/builder/runtime');
    UnpackingService = (builder as any).UnpackingService;

    if (!UnpackingService) {
      const packing = await import('@standardagents/builder');
      UnpackingService = (packing as any).UnpackingService;
    }
  } catch (err) {
    logger.error('Failed to load unpacking service from @standardagents/builder');
    logger.info('Make sure @standardagents/builder is installed.');
    process.exit(1);
  }

  const unpackingService = new UnpackingService();

  // If no package identifier provided, list available packages
  if (!pkgIdentifier) {
    const packages = await unpackingService.listPackages(rootDir);

    if (packages.length === 0) {
      logger.info('No packed agents found.');
      logger.info('Pack an agent with: agents pack <agent-name>');
      process.exit(0);
    }

    logger.info('Available packed agents:');
    for (const pkg of packages) {
      console.log(`  - ${pkg.packageId} (${pkg.version}) [${pkg.source}]`);
      console.log(`    Entry: ${pkg.entryAgent}`);
      console.log(`    Path:  ${pkg.path}`);
      console.log('');
    }
    logger.info('Run: agents unpack <package-id>');
    process.exit(0);
  }

  logger.info(`Unpacking: ${pkgIdentifier}`);

  // Execute unpacking
  const result = await unpackingService.unpack({
    package: pkgIdentifier,
    rootDir,
    keepSignatures: !options.noSignatures,
  });

  if (!result.success) {
    logger.error(`Unpacking failed: ${result.error}`);
    process.exit(1);
  }

  // Show result
  console.log('');
  logger.success('Unpacking complete!');
  console.log(`  Package:    ${result.packageId}`);
  console.log(`  Files:      ${result.filesCreated.length} created`);

  if (result.filesCreated.length > 0) {
    console.log('');
    logger.info('Created files:');
    for (const file of result.filesCreated) {
      const relPath = path.relative(rootDir, file);
      console.log(`  - ${relPath}`);
    }
  }

  if (result.warnings.length > 0) {
    console.log('');
    logger.warning('Warnings:');
    for (const warning of result.warnings) {
      console.log(`  - ${warning}`);
    }
  }

  if (options.noSignatures) {
    console.log('');
    logger.info('Package signatures removed - files are fully editable.');
  } else {
    console.log('');
    logger.info('Package signatures preserved - files are editable but marked as packed.');
  }
}

/**
 * List all packed agents.
 */
export async function listPacked() {
  // Verify this is a Standard Agents project
  if (!isStandardAgentsProject()) {
    logger.error('This does not appear to be a Standard Agents project.');
    logger.info('Make sure you have @standardagents/builder installed and an agents/ directory.');
    process.exit(1);
  }

  const rootDir = process.cwd();

  // Dynamic import of unpacking service
  let UnpackingService: any;
  try {
    const builder = await import('@standardagents/builder/runtime');
    UnpackingService = (builder as any).UnpackingService;

    if (!UnpackingService) {
      const packing = await import('@standardagents/builder');
      UnpackingService = (packing as any).UnpackingService;
    }
  } catch (err) {
    logger.error('Failed to load unpacking service from @standardagents/builder');
    logger.info('Make sure @standardagents/builder is installed.');
    process.exit(1);
  }

  const unpackingService = new UnpackingService();
  const packages = await unpackingService.listPackages(rootDir);

  if (packages.length === 0) {
    logger.info('No packed agents found.');
    logger.info('Pack an agent with: agents pack <agent-name>');
    return;
  }

  logger.info('Discovered packed agents:\n');

  for (const pkg of packages) {
    console.log(`${pkg.packageId} (${pkg.version})`);
    console.log(`  Source:  ${pkg.source}`);
    console.log(`  Entry:   ${pkg.entryAgent}`);
    console.log(`  Path:    ${pkg.path}`);
    console.log('');
  }
}
